import { Resolver, UseFormReturn } from 'react-hook-form';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { IDocumentData } from '@models/document/IDocumentData';
import { IFieldElem } from '@models/IFormData';
import { IDebugValidationFlags } from './evaluateFieldErrors';
import { createFormulaManagerEvaluator } from './formulaEvaluator';
import { evaluateDocumentField, evaluateRequiredControl, type HolidayCheckerProvider } from './evaluateField';
import {
    buildDocumentValidationIndex,
    normalizeDocumentFieldPath,
    type IDocumentValidationIndex,
} from './validationIndex';
import type { ValidatableControl } from './schemeWalk';
import {
    countEffectiveAttachments,
    getAttachmentsAnchorId,
    getAttachmentsRequiredMessage,
} from './attachmentsValidation';
import { FormulaRequestCache } from './formulaRequestCache';
import { createDocumentTableFormulaEvaluator } from './tableFormulaEvaluator';
import { validateDocumentTable } from './tableValidation';
import type { IFormulaRequestCache } from '@utils/managers/IFormulaRequestCache';
import type { IFieldOverrides } from '@/context/DebugPanelContext';
import { overrideTableColumns } from '@utils/debug/overrideColumn';

export interface IDocumentResolverRunMetric {
    mode: 'full' | 'partial';
    outcome: 'success' | 'evaluator-fallback' | 'resolver-error';
    durationMs: number;
    selectedPathCount: number;
    totalPathCount: number;
    formulaEvaluationCount: number;
    networkRequestCount: number;
    validationErrorCount: number;
    evaluatorErrorCount: number;
}

/**
 * Контекст resolver-а валидации документа. Передаётся в resolver через стабильный
 * getter (см. {@link makeDocumentResolver}) — это надёжнее, чем полагаться на
 * реактивность `context` в react-hook-form: getter всегда читает свежее значение.
 */
export interface IDocumentResolverContext {
    scheme: IDocumentScheme;
    /** Runtime-карта key → {index, name, value} (fieldsData в Document.tsx). */
    fields: Record<string, IFieldElem>;
    isEdit: boolean;
    isNew: boolean;
    debug?: IDebugValidationFlags;
    /** Поставщик проверки выходного дня (рабочий календарь). */
    getHolidayChecker?: HolidayCheckerProvider;
    /** Предрасчитанный индекс контролов и их формульных зависимостей. */
    validationIndex?: IDocumentValidationIndex;
    /** Явный submit-сигнал: ResolverOptions не различает submit и trigger всех mounted names. */
    shouldValidateAll?: () => boolean;
    /** Документ + пользователь: смена scope немедленно очищает сетевой TTL-cache. */
    requestCacheScope?: string;
    /** Обезличенная агрегированная метрика одного resolver-run. */
    onValidationRun?: (metric: IDocumentResolverRunMetric) => void;
    /**
     * Императивные валидаторы mount-bound контролов (вложения: allowedExtensions через
     * `ctl.isValid()`). RHF игнорирует register-level `validate` при наличии resolver,
     * поэтому такие проверки прогоняет сам resolver в конце каждого запуска.
     * Ключ — путь формы (напр. `files`), `false` — путь невалиден.
     */
    getImperativeValidators?: () => ReadonlyMap<string, () => boolean | undefined>;
    /**
     * Дебаг-оверрайды правил из «жучка»-редактора реквизита: ключ — key контрола.
     * Резолвер применяет их так же, как контролы в UI (Field.tsx/overrideColumn) — иначе
     * оверрайд менял бы звёздочку/виджет, но не результат валидации.
     */
    fieldOverrides?: ReadonlyMap<string, IFieldOverrides>;
}

/** Сообщение для невалидной таблицы (ячейки уже подсвечены красным в гриде). */
const TABLE_INVALID_MESSAGE = 'Проверьте заполнение таблицы';

/** Ошибка одного пути: текст, вкладка для перехода и (для блока вложений) DOM-якорь. */
interface IResolverPathError {
    message: string;
    documentTabId: string;
    /** Цель прокрутки для контрола без `fields[].index` (блок вложений). */
    documentAnchorId?: string;
}

/** Результат валидации одного пути: `[path, ошибка | undefined]`. */
type ResolverPathResult = readonly [string, IResolverPathError | undefined];

/**
 * Положить ошибку поля по пути `fields.[index].value` в дерево ошибок RHF.
 * `ref.name` задаём для совместимости формы ошибок с нативным деревом RHF —
 * на него опирается diagnostics-обработчик docInvalidFormHandler (helpers.ts),
 * который иначе упал бы на отсутствии ref. Разреженный массив; ключи = index.
 */
function setFieldError(errors: Record<string, any>, index: number, message: string, documentTabId?: string): void {
    if (!errors.fields) errors.fields = [];
    errors.fields[index] = {
        value: {
            type: 'validate',
            message,
            ref: { name: `fields.[${index}].value` },
            ...(documentTabId ? { documentTabId } : {}),
        },
    };
}

/**
 * Положить ошибку контрола, у которого нет `fields[].index` (блок вложений), по его
 * собственному пути формы. `ref.name` — как у полевых ошибок, ради docInvalidFormHandler.
 */
function setPathError(errors: Record<string, any>, path: string, error: IResolverPathError): void {
    errors[path] = {
        type: 'required',
        message: error.message,
        ref: { name: path },
        documentTabId: error.documentTabId,
        ...(error.documentAnchorId ? { documentAnchorId: error.documentAnchorId } : {}),
    };
}

/**
 * Прогнать императивные валидаторы (см. {@link IDocumentResolverContext.getImperativeValidators})
 * и дописать их ошибки в дерево RHF. Форма ошибки повторяет register-level `validate`
 * (`type: 'checkValid'`, пустое сообщение — блок вложений показывает свои ошибки сам).
 */
function applyImperativeValidators(ctx: IDocumentResolverContext, errors: Record<string, any>): void {
    const validators = ctx.getImperativeValidators?.();
    if (!validators?.size) return;
    for (const [path, validate] of validators) {
        let valid: boolean | undefined;
        try {
            valid = validate();
        } catch (error) {
            console.error(`Императивный валидатор "${path}" упал:`, error);
            valid = true; // fail-open, как catch в легаси checkValid
        }
        if (valid === false) {
            const parts = path.split('.');
            let node = errors;
            for (const part of parts.slice(0, -1)) {
                node = node[part] ?? (node[part] = {});
            }
            const leaf = parts[parts.length - 1];
            // Пустое сообщение `checkValid` не должно вытеснять required-ошибку того же
            // пути: она единственная, которую пользователь читает в панели и в блоке.
            if (node[leaf]) continue;
            node[leaf] = { type: 'checkValid', message: '', ref: { name: path } };
        }
    }
}

/**
 * getValues для FormulaManager поверх объекта значений формы (1-й аргумент resolver-а).
 * Эквивалентно `formMethods.getValues` (оба читают _formValues), но не требует
 * самого formMethods — резолвер получает значения напрямую.
 */
function makeResolverGetValues(values: unknown): (name?: string) => any {
    return (name?: string): any => {
        if (!name) return values;
        return name.split('.').reduce<any>((acc, part) => {
            if (acc == null) return undefined;
            const bracket = /^\[(\d+)\]$/.exec(part);
            return bracket ? acc[Number(bracket[1])] : acc[part];
        }, values);
    };
}

/**
 * Фабрика custom async RHF resolver для формы документа.
 *
 * `getContext` вызывается на каждой валидации и возвращает актуальный контекст
 * (scheme/fields/isEdit/isNew/debug). Если контекста нет или режим просмотра —
 * валидация не выполняется (паритет с текущим поведением).
 *
 * При blur/trigger валидирует только имена из `ResolverOptions.names`. Полный submit
 * помечается явным сигналом контекста и получает полный проход. Ошибки остальных полей
 * хранятся в snapshot, чтобы частичный прогон не стирал их результат.
 *
 * НЕ заменяет: `ValidatorManager` (scheme-валидаторы), редакторные проверки DevExtreme
 * и вложенные SubForm — это отдельные слои.
 */
export function makeDocumentResolver(getContext: () => IDocumentResolverContext | undefined): Resolver<IDocumentData> {
    let fallbackIndex:
        { scheme: IDocumentScheme; fields: Record<string, IFieldElem>; value: IDocumentValidationIndex } | undefined;
    let activeState:
        | {
              index: IDocumentValidationIndex;
              scheme: IDocumentScheme;
              fields: Record<string, IFieldElem>;
              isEdit: boolean;
              isNew: boolean;
              requestCacheScope?: string;
              epoch: number;
          }
        | undefined;
    let epoch = 0;
    let runId = 0;
    const messagesByPath = new Map<string, { message: string; documentTabId: string }>();
    const latestRunByPath = new Map<string, number>();
    const latestPromiseByPath = new Map<string, Promise<void>>();
    const formulaRequestCache = new FormulaRequestCache();

    /**
     * После завершения текущего прогона часть его путей мог перехватить более свежий запуск.
     * Ждём их промисы, чтобы вернуть RHF актуальный снапшот ошибок (last-run-wins), а не
     * устаревший результат вытесненного прогона. Цикл — на случай, если во время ожидания
     * путь снова сменит владельца. Выходим, как только пересекающихся свежих запусков не
     * осталось либо эпоха контекста сменилась (снапшот всё равно уже сброшен).
     */
    const waitForSupersedingRuns = async (
        selectedPaths: readonly string[],
        currentRunId: number,
        currentEpoch: number,
    ): Promise<void> => {
        while (activeState?.epoch === currentEpoch) {
            const superseding = new Map<string, Promise<void>>();
            for (const path of selectedPaths) {
                if (latestRunByPath.get(path) === currentRunId) continue;
                const promise = latestPromiseByPath.get(path);
                if (promise) superseding.set(path, promise);
            }
            if (superseding.size === 0) return;

            await Promise.all(new Set(superseding.values()));
            const rearmed = [...superseding].some(([path, promise]) => latestPromiseByPath.get(path) !== promise);
            if (!rearmed) return;
        }
    };

    const reportRun = (ctx: IDocumentResolverContext, metric: IDocumentResolverRunMetric): void => {
        try {
            ctx.onValidationRun?.(metric);
        } catch (error) {
            console.error('Document resolver telemetry failed', error);
        }
    };

    const getValidationIndex = (ctx: IDocumentResolverContext): IDocumentValidationIndex => {
        if (ctx.validationIndex) return ctx.validationIndex;
        if (fallbackIndex?.scheme === ctx.scheme && fallbackIndex.fields === ctx.fields) return fallbackIndex.value;

        const value = buildDocumentValidationIndex(ctx.scheme, ctx.fields);
        fallbackIndex = { scheme: ctx.scheme, fields: ctx.fields, value };
        return value;
    };

    const resetSnapshotIfNeeded = (ctx: IDocumentResolverContext, index: IDocumentValidationIndex): number => {
        if (
            activeState?.index === index &&
            activeState.scheme === ctx.scheme &&
            activeState.fields === ctx.fields &&
            activeState.isEdit === ctx.isEdit &&
            activeState.isNew === ctx.isNew &&
            activeState.requestCacheScope === ctx.requestCacheScope
        ) {
            return activeState.epoch;
        }

        epoch += 1;
        activeState = {
            index,
            scheme: ctx.scheme,
            fields: ctx.fields,
            isEdit: ctx.isEdit,
            isNew: ctx.isNew,
            requestCacheScope: ctx.requestCacheScope,
            epoch,
        };
        messagesByPath.clear();
        latestRunByPath.clear();
        latestPromiseByPath.clear();
        formulaRequestCache.clear();
        return epoch;
    };

    const getRequestedPaths = (index: IDocumentValidationIndex, names: readonly string[] | undefined): string[] => {
        if (!names?.length) return [...index.paths];

        const requested = new Set<string>();
        for (const rawName of names) {
            const name = normalizeDocumentFieldPath(rawName);
            if (index.controlsByPath.has(name)) {
                requested.add(name);
                continue;
            }

            // Изменение вложенного значения таблицы относится к контролу таблицы.
            const parent = index.paths.find((path) => name.startsWith(`${path}.`));
            if (parent) requested.add(parent);
        }
        return [...requested];
    };

    const applyPathError = (
        ctx: IDocumentResolverContext,
        index: IDocumentValidationIndex,
        errors: Record<string, any>,
        path: string,
        validationError: IResolverPathError,
    ) => {
        const control = index.controlsByPath.get(path)?.[0];
        if (control?.kind === 'attachments') {
            setPathError(errors, path, validationError);
            return;
        }
        const elem = control && ctx.fields[control.key];
        if (elem && elem.index >= 0) {
            setFieldError(errors, elem.index, validationError.message, validationError.documentTabId);
        }
    };

    const makeErrorsSnapshot = (ctx: IDocumentResolverContext, index: IDocumentValidationIndex) => {
        const errors: Record<string, any> = {};
        for (const path of index.paths) {
            const validationError = messagesByPath.get(path);
            if (validationError) applyPathError(ctx, index, errors, path, validationError);
        }
        return errors;
    };

    // Полный (submit) прогон строит ошибки из СВОИХ результатов, а не из общего messagesByPath:
    // RHF handleSubmit делает wholesale-replace errors и не должен увидеть снапшот, очищенный
    // параллельным partial-прогоном (смена ссылки fields → resetSnapshotIfNeeded → messagesByPath.clear()).
    const makeErrorsFromResults = (
        ctx: IDocumentResolverContext,
        index: IDocumentValidationIndex,
        results: readonly ResolverPathResult[],
    ) => {
        const errors: Record<string, any> = {};
        for (const [path, validationError] of results) {
            if (validationError) applyPathError(ctx, index, errors, path, validationError);
        }
        return errors;
    };

    const evaluateControl = async (
        control: ValidatableControl,
        ctx: IDocumentResolverContext,
        values: IDocumentData,
        evaluator: ReturnType<typeof createFormulaManagerEvaluator>,
        tableEvaluator: ReturnType<typeof createDocumentTableFormulaEvaluator>,
    ): Promise<string | undefined> => {
        // Блок вложений адресуется путём формы, а не индексом поля, — разбираем его
        // до обращения к fields[control.key] (записи там нет).
        if (control.kind === 'attachments') {
            for (const rule of control.visibilityChain) {
                if (!(await evaluator.evalBool(rule, true))) return undefined;
            }
            if (ctx.debug?.skipAllValidation || ctx.debug?.skipRequiredValidation) return undefined;
            if (!(await evaluator.evalBool(control.table.required, false))) return undefined;
            return countEffectiveAttachments(values?.files) > 0
                ? undefined
                : getAttachmentsRequiredMessage(control.table);
        }

        const elem = ctx.fields[control.key];
        if (!elem || elem.index === undefined || elem.index < 0) return undefined;

        // Видимость наследуется: если любой контейнер-предок скрыт, поле не валидируется.
        for (const rule of control.visibilityChain) {
            if (!(await evaluator.evalBool(rule, true))) return undefined;
        }

        const override = ctx.fieldOverrides?.get(control.key);
        const value = values?.fields?.[elem.index]?.value;
        if (control.kind === 'field') {
            return evaluateDocumentField(control.field, value, evaluator, {
                debug: ctx.debug,
                getHolidayChecker: ctx.getHolidayChecker,
                override,
            });
        }
        if (control.kind === 'required') {
            return evaluateRequiredControl(control.control, value, evaluator, {
                debug: ctx.debug,
                override,
                controlType: control.controlType,
            });
        }

        const table = overrideTableColumns(control.table, ctx.fieldOverrides);
        const result = await validateDocumentTable(table, value, tableEvaluator, ctx.debug);
        return result.valid ? undefined : TABLE_INVALID_MESSAGE;
    };

    return async (values, _context, options) => {
        const ctx = getContext();
        if (!ctx || (!ctx.isEdit && !ctx.isNew)) {
            return { values, errors: {} };
        }

        const index = getValidationIndex(ctx);
        const currentEpoch = resetSnapshotIfNeeded(ctx, index);
        const mode = ctx.shouldValidateAll?.() ? 'full' : 'partial';
        const selectedPaths = mode === 'full' ? [...index.paths] : getRequestedPaths(index, options.names);
        if (selectedPaths.length === 0) {
            // Сюда попадает и trigger по пути вне индекса (напр. `files` вложений) —
            // императивные валидаторы обязаны отработать и в этой ветке.
            const errors = makeErrorsSnapshot(ctx, index);
            applyImperativeValidators(ctx, errors);
            return Object.keys(errors).length > 0 ? { values: {}, errors: errors as any } : { values, errors: {} };
        }

        const startedAt = performance.now();
        let formulaEvaluationCount = 0;
        let networkRequestCount = 0;
        let evaluatorErrorCount = 0;
        const observedRequestCache: IFormulaRequestCache = {
            getOrCreate: (operation, args, loader) =>
                formulaRequestCache.getOrCreate(operation, args, () => {
                    networkRequestCount += 1;
                    return loader();
                }),
        };
        const makeMetric = (outcome: IDocumentResolverRunMetric['outcome']): IDocumentResolverRunMetric => ({
            mode,
            outcome,
            durationMs: performance.now() - startedAt,
            selectedPathCount: selectedPaths.length,
            totalPathCount: index.paths.length,
            formulaEvaluationCount,
            networkRequestCount,
            validationErrorCount: messagesByPath.size,
            evaluatorErrorCount,
        });

        try {
            const getValues = makeResolverGetValues(values);
            const onFormulaEvaluation = () => {
                formulaEvaluationCount += 1;
            };
            const onFormulaError = () => {
                evaluatorErrorCount += 1;
            };
            const evaluator = createFormulaManagerEvaluator(
                ctx.fields,
                { getValues } as unknown as UseFormReturn<any>,
                ctx.isEdit,
                ctx.isNew,
                observedRequestCache,
                onFormulaEvaluation,
                onFormulaError,
            );
            const tableEvaluator = createDocumentTableFormulaEvaluator(
                ctx.fields,
                getValues,
                ctx.isEdit,
                ctx.isNew,
                observedRequestCache,
                onFormulaEvaluation,
                onFormulaError,
            );
            const currentRunId = ++runId;
            let ownResults: readonly ResolverPathResult[] | undefined;
            const evaluation = Promise.all(
                selectedPaths.map(async (path) => {
                    const controls = index.controlsByPath.get(path)!;
                    for (const control of controls) {
                        const message = await evaluateControl(control, ctx, values, evaluator, tableEvaluator);
                        if (message)
                            return [
                                path,
                                {
                                    message,
                                    documentTabId: control.tabId,
                                    ...(control.kind === 'attachments'
                                        ? { documentAnchorId: getAttachmentsAnchorId(control.table) }
                                        : {}),
                                },
                            ] as const;
                    }
                    return [path, undefined] as const;
                }),
            );
            const completion = evaluation.then(
                (results) => {
                    ownResults = results;
                    if (activeState?.epoch !== currentEpoch) return;
                    for (const [path, message] of results) {
                        if (latestRunByPath.get(path) !== currentRunId) continue;
                        if (message) messagesByPath.set(path, message);
                        else messagesByPath.delete(path);
                    }
                },
                (error) => {
                    const stillOwnsPath =
                        activeState?.epoch === currentEpoch &&
                        selectedPaths.some((path) => latestRunByPath.get(path) === currentRunId);
                    if (stillOwnsPath) throw error;
                },
            );
            for (const path of selectedPaths) {
                latestRunByPath.set(path, currentRunId);
                latestPromiseByPath.set(path, completion);
            }

            await completion;

            // Если тот же путь уже проверяет более свежий запуск, старый resolver не должен
            // вернуть RHF устаревшую ошибку. Ждём только пересекающиеся новые запуски.
            await waitForSupersedingRuns(selectedPaths, currentRunId, currentEpoch);

            // full = submit: возвращаем СВОИ результаты (полный проход по всем путям), чтобы гонка
            // epoch с параллельным partial-прогоном не подсунула RHF пустой снапшот на wholesale-replace.
            const errors =
                mode === 'full' && ownResults
                    ? makeErrorsFromResults(ctx, index, ownResults)
                    : makeErrorsSnapshot(ctx, index);
            applyImperativeValidators(ctx, errors);
            reportRun(ctx, makeMetric(evaluatorErrorCount > 0 ? 'evaluator-fallback' : 'success'));
            return Object.keys(errors).length > 0 ? { values: {}, errors: errors as any } : { values, errors: {} };
        } catch (error) {
            reportRun(ctx, makeMetric('resolver-error'));
            throw error;
        }
    };
}
